import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { infographicInfo, infographicImageUrl } from "@/lib/api";

/* AI-generated infographic panel for any feature page.
 * Renders:
 *   – the polished Gemini Nano Banana infographic (large hero image)
 *   – accessible fallback grid of the same features (for a11y + no-image fallback)
 *   – a subtle re-generate button if a query param ?regen=1 is present.
 */
export default function FeatureInfographic({ slug, accent = "#4F46E5" }) {
  const [meta, setMeta] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const m = await infographicInfo(slug);
        if (mounted) setMeta(m);
      } catch (e) {
        // catalog might not include this slug — silently drop the panel
      } finally {
        if (mounted) setBusy(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (busy) {
    return (
      <div className="bg-slate-950 rounded-2xl p-10 flex items-center justify-center border border-white/10" data-testid={`infographic-${slug}-loading`}>
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
      </div>
    );
  }
  if (!meta) return null;

  return (
    <section
      className="relative rounded-3xl overflow-hidden border border-white/10 bg-slate-950"
      data-testid={`infographic-${slug}`}
      style={{ boxShadow: `0 30px 60px -20px ${accent}55` }}
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        background: `radial-gradient(circle at 20% 10%, ${accent}80, transparent 45%), radial-gradient(circle at 90% 90%, ${accent}40, transparent 55%)`,
      }} />

      <div className="relative p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white/80 border border-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> AI-generated infographic
          </span>
          <span className="text-xs text-white/50">Rendered live with Gemini Nano Banana from this module's real feature list.</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Image */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-white/10 bg-black/60">
            {imgErr ? (
              <div className="aspect-[16/9] flex items-center justify-center text-white/50 text-sm p-8 text-center">
                Infographic will render here once the AI image renders. Refresh in a moment.
              </div>
            ) : (
              <img
                src={infographicImageUrl(slug)}
                alt={`${meta.title} — AI-generated infographic`}
                loading="lazy"
                onError={() => setImgErr(true)}
                className="w-full h-auto object-cover"
                data-testid={`infographic-${slug}-img`}
              />
            )}
          </div>

          {/* Feature list (fallback + a11y) */}
          <div className="text-white">
            <div className="text-xs uppercase tracking-widest text-white/50" style={{ color: accent }}>{meta.tagline}</div>
            <h3 className="mt-2 text-2xl font-bold leading-tight">{meta.title}</h3>
            <ul className="mt-5 space-y-2.5">
              {meta.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/85">
                  <span
                    className="mt-0.5 flex-shrink-0 inline-flex w-5 h-5 rounded-full items-center justify-center text-[11px] font-bold"
                    style={{ background: accent, color: "#0B1220" }}
                  >
                    {i + 1}
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
